/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type {
  LogDetails,
  TPendingDiagnosticLog,
  TPendingTelemetryLog
} from "../types";
import { TelemetryLogType } from "../types";
import { StorageAdapter } from "../storage/StorageAdapter";
import {
  STORAGE_KEY_PENDING_DIAGNOSTIC_LOGS,
  STORAGE_KEY_PENDING_TELEMETRY_LOGS
} from "../storage/storageConfig";
import { Log } from "./Log";

const TAG: string = "PendingTelemetryStore";

/**
 * Overflow drops the oldest entries, so this cap has to cover everything the
 * app emits before provisioning: PreSignIn startup plus the whole sign-in
 * phase, across all three telemetry streams. A DBS license-plate sign-in is
 * the worst case — the user walks to their phone while the client polls —
 * which is why its poll diagnostics are sampled at the call site rather than
 * logged per tick.
 */
const MAX_PENDING_LOGS: number = 250;

/**
 * Persists telemetry in IndexedDB while Sage identity (bodyId / partnerId) is
 * unavailable, for all three streams. Entries are stored as one ordered array
 * under a single storage key so the existing StorageAdapter and schema are
 * reused without a version bump.
 *
 * Every method swallows its own errors: telemetry must never break the app.
 */
export class PendingTelemetryStore {
  private static mNextSequence: number = 0;
  private static mSequenceInitialised: boolean = false;
  private static mLegacyDrained: boolean = false;

  /**
   * Moves entries written by an older build under the diagnostics-only key
   * onto the current key, once, then deletes the old key. Without this, logs
   * captured during the boot that installed the new build would be stranded.
   */
  private static async drainLegacyKey(): Promise<void> {
    if (PendingTelemetryStore.mLegacyDrained) {
      return;
    }
    PendingTelemetryStore.mLegacyDrained = true;
    try {
      const storage: StorageAdapter = StorageAdapter.getInstance();
      const legacy: TPendingDiagnosticLog[] | null = await storage.get<
        TPendingDiagnosticLog[]
      >(STORAGE_KEY_PENDING_DIAGNOSTIC_LOGS);
      if (legacy === null || legacy.length === 0) {
        return;
      }

      const migrated: TPendingTelemetryLog[] = legacy.map(
        (entry: TPendingDiagnosticLog): TPendingTelemetryLog => ({
          sequence: entry.sequence,
          timestamp: entry.timestamp,
          logType: TelemetryLogType.Diagnostic,
          name: entry.name,
          message: entry.message,
          severity: entry.severity,
          details: entry.details
        })
      );

      const existing: TPendingTelemetryLog[] | null = await storage.get<
        TPendingTelemetryLog[]
      >(STORAGE_KEY_PENDING_TELEMETRY_LOGS);
      const merged: TPendingTelemetryLog[] =
        existing !== null ? [...migrated, ...existing] : migrated;

      await PendingTelemetryStore.write(merged);
      await storage.delete(STORAGE_KEY_PENDING_DIAGNOSTIC_LOGS);
      Log.i(
        TAG,
        "drainLegacyKey(): Migrated " +
          String(migrated.length) +
          " diagnostic log(s)"
      );
    } catch (error: unknown) {
      Log.w(TAG, "drainLegacyKey()", error);
    }
  }

  private static async ensureSequence(): Promise<void> {
    if (PendingTelemetryStore.mSequenceInitialised) {
      return;
    }
    try {
      await PendingTelemetryStore.drainLegacyKey();
      const logs: TPendingTelemetryLog[] | null =
        await PendingTelemetryStore.readAll();
      if (logs !== null && logs.length > 0) {
        const maxSeq: number = logs.reduce(
          (max: number, entry: TPendingTelemetryLog) =>
            entry.sequence > max ? entry.sequence : max,
          0
        );
        PendingTelemetryStore.mNextSequence = maxSeq + 1;
      }
    } catch (error: unknown) {
      Log.w(TAG, "ensureSequence() — falling back to 0", error);
    }
    PendingTelemetryStore.mSequenceInitialised = true;
  }

  /** Queues one diagnostic event for replay. */
  public static async enqueueDiagnostic(
    name: string,
    message: string,
    severity: number,
    details?: LogDetails
  ): Promise<void> {
    await PendingTelemetryStore.enqueue({
      logType: TelemetryLogType.Diagnostic,
      name,
      message,
      severity,
      details
    });
  }

  /** Queues one operations event for replay. */
  public static async enqueueOperational(
    name: string,
    details?: LogDetails
  ): Promise<void> {
    await PendingTelemetryStore.enqueue({
      logType: TelemetryLogType.Operational,
      name,
      details
    });
  }

  /** Queues one analytics event for replay. */
  public static async enqueueAnalytics(
    name: string,
    eventType?: string,
    details?: LogDetails
  ): Promise<void> {
    await PendingTelemetryStore.enqueue({
      logType: TelemetryLogType.Analytics,
      name,
      eventType,
      details
    });
  }

  private static async enqueue(
    entry: Omit<TPendingTelemetryLog, "sequence" | "timestamp">
  ): Promise<void> {
    try {
      await PendingTelemetryStore.ensureSequence();
      const stamped: TPendingTelemetryLog = {
        ...entry,
        sequence: PendingTelemetryStore.mNextSequence,
        timestamp: Date.now()
      };
      PendingTelemetryStore.mNextSequence += 1;

      const existing: TPendingTelemetryLog[] | null =
        await PendingTelemetryStore.readAll();
      const logs: TPendingTelemetryLog[] =
        existing !== null ? [...existing, stamped] : [stamped];

      // Cap the queue size — drop oldest entries on overflow
      const trimmed: TPendingTelemetryLog[] =
        logs.length > MAX_PENDING_LOGS
          ? logs.slice(logs.length - MAX_PENDING_LOGS)
          : logs;

      await PendingTelemetryStore.write(trimmed);
      Log.d(
        TAG,
        "enqueue(): " +
          entry.logType +
          " queued, pending count=" +
          String(trimmed.length)
      );
    } catch (error: unknown) {
      Log.e(TAG, "enqueue()", error);
    }
  }

  private static async write(logs: TPendingTelemetryLog[]): Promise<void> {
    const storage: StorageAdapter = StorageAdapter.getInstance();
    await storage.set<TPendingTelemetryLog[]>(
      STORAGE_KEY_PENDING_TELEMETRY_LOGS,
      logs,
      Number.POSITIVE_INFINITY
    );
  }

  public static async readAll(): Promise<TPendingTelemetryLog[] | null> {
    try {
      const storage: StorageAdapter = StorageAdapter.getInstance();
      return await storage.get<TPendingTelemetryLog[]>(
        STORAGE_KEY_PENDING_TELEMETRY_LOGS
      );
    } catch (error: unknown) {
      Log.e(TAG, "readAll()", error);
      return null;
    }
  }

  public static async removeBySequences(sequences: number[]): Promise<void> {
    try {
      const storage: StorageAdapter = StorageAdapter.getInstance();
      const existing: TPendingTelemetryLog[] | null = await storage.get<
        TPendingTelemetryLog[]
      >(STORAGE_KEY_PENDING_TELEMETRY_LOGS);
      if (existing === null || existing.length === 0) {
        return;
      }
      const sequenceSet: Set<number> = new Set(sequences);
      const remaining: TPendingTelemetryLog[] = existing.filter(
        (entry: TPendingTelemetryLog) => !sequenceSet.has(entry.sequence)
      );
      if (remaining.length === 0) {
        await storage.delete(STORAGE_KEY_PENDING_TELEMETRY_LOGS);
      } else {
        await PendingTelemetryStore.write(remaining);
      }
    } catch (error: unknown) {
      Log.e(TAG, "removeBySequences()", error);
    }
  }

  public static async clear(): Promise<void> {
    try {
      const storage: StorageAdapter = StorageAdapter.getInstance();
      await storage.delete(STORAGE_KEY_PENDING_TELEMETRY_LOGS);
      Log.d(TAG, "clear(): Pending telemetry cleared");
    } catch (error: unknown) {
      Log.e(TAG, "clear()", error);
    }
  }

  /** Reset static state — test-only. */
  public static resetForTest(): void {
    PendingTelemetryStore.mNextSequence = 0;
    PendingTelemetryStore.mSequenceInitialised = false;
    PendingTelemetryStore.mLegacyDrained = false;
  }
}
